import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { overviewService } from '@/features/overview/services/overviewService';
import { configureHttpAuthHandlers } from '@/shared/api/httpClient';
import { logger } from '@/shared/observability/logger';
import { isApiError } from '@/shared/api/errors';
import { routes } from './routes';

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, saved) {
    if (saved) return saved;
    if (to.hash) return { el: to.hash };
    return { top: 0 };
  },
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const requiresAuth = to.matched.some((r) => r.meta.requiresAuth);
  const requiresTenantAdmin = to.matched.some((r) => r.meta.requiresTenantAdmin);

  logger.debug({ event: 'route.transition', to: to.fullPath, name: to.name }, 'route transition');

  if (requiresAuth && !auth.isAuthenticated) {
    logger.info({ event: 'route.guard.unauth', to: to.fullPath }, 'redirecting to login');
    return { name: 'auth.login', query: { from: to.fullPath } };
  }

  // Friendly redirect away from auth pages when already signed in.
  if (!requiresAuth && auth.isAuthenticated && (to.name === 'auth.login.index' || to.name === 'auth.verify')) {
    return { name: 'overview' };
  }

  if (requiresTenantAdmin && !auth.isTenantAdmin) {
    try {
      // Probe the admin overview endpoint — backend's requireTenantAdmin will 403 non-admins.
      await overviewService.fetch();
      auth.setTenantAdmin(true);
    } catch (err) {
      if (isApiError(err) && err.isForbidden) {
        logger.warn({ event: 'route.guard.not_admin', to: to.fullPath }, 'user is not a tenant admin');
        return { name: 'auth.unauthorized' };
      }
      if (isApiError(err) && err.isUnauthorized) {
        auth.clear();
        return { name: 'auth.login' };
      }
      // Other errors — let the page render and show its own error UI.
      logger.warn({ event: 'route.guard.probe_error', err }, 'admin probe failed');
    }
  }

  return true;
});

/**
 * After a new deploy the old page chunks (e.g. `LeaguesPage-DlipUCw5.js`) are
 * gone, and the host answers the missing file with index.html (text/html), so
 * an open tab can no longer navigate ("Failed to fetch dynamically imported
 * module"). Recover by loading the target route fresh, which pulls the new
 * build. Guarded so a genuinely broken chunk can't cause a reload loop.
 */
const CHUNK_RELOAD_KEY = 'cc-admin:chunk-reload-at';
function isStaleChunkError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Expected a JavaScript/i.test(
    msg,
  );
}
function reloadOnceFor(path: string): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    if (Date.now() - last < 10_000) return false; // already tried just now
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable — still try once.
  }
  window.location.assign(path);
  return true;
}

router.onError((err, to) => {
  if (isStaleChunkError(err)) {
    logger.warn({ event: 'route.stale_chunk', to: to?.fullPath }, 'stale build chunk, reloading');
    reloadOnceFor(to?.fullPath ?? window.location.pathname);
  }
});

// Vite's own signal for a failed preload of a lazy chunk.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  reloadOnceFor(window.location.pathname + window.location.search);
});

router.afterEach((to) => {
  const title = (to.meta.title as string | undefined) ?? 'Admin';
  document.title = `${title} · Challengers Crease Admin`;
});

/** Wire HTTP 401/403 → router redirects. Call once at startup after stores are ready. */
export function bindHttpAuthHandlersToRouter() {
  const auth = useAuthStore();
  configureHttpAuthHandlers({
    onUnauthorized: () => {
      auth.clear();
      // Use replace to avoid stacking history entries on session-expiry storms.
      void router.replace({ name: 'auth.login' });
    },
    onForbidden: () => {
      // Soft signal — leave to per-page handling, but mark admin status as false
      // so the next route guard probe re-runs.
      auth.setTenantAdmin(false);
    },
  });
}
